/**
 * 新增记录弹窗组件
 * 在项目概览中复用原有的新建表单能力
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, AutoComplete, message, ConfigProvider } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import WorkerAutoComplete, { WorkerSearchResult } from '@/components/WorkerAutoComplete';
import { useLeaders } from '@/modules/progress/hooks';
import { ProjectService } from '@/services/projectService';
import EquipmentUsageForm from '@/pages/Equipment/EquipmentUsageForm';
import MaterialUsageForm from '@/pages/Material/MaterialUsageForm';
import { themeConfig } from '@/theme/themeConfig';

export type RecordType = 'worktime' | 'equipment' | 'material';

interface AddRecordModalProps {
  visible: boolean;
  recordType: RecordType | null;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
  isMobile?: boolean;
}

interface WorktimeFormValues {
  workDate: dayjs.Dayjs;
  workerName: string;
  projectName: string;
  workContent: string;
  workUnits: number;
  unitPrice: number;
  constructionType?: string;
  leaderId?: string;
}

const AddRecordModal: React.FC<AddRecordModalProps> = ({
  visible,
  recordType,
  projectId,
  projectName,
  onClose,
  onSuccess,
  isMobile = false,
}) => {
  const [worktimeForm] = Form.useForm<WorktimeFormValues>();
  const queryClient = useQueryClient();
  const [newRecordWorkerId, setNewRecordWorkerId] = useState<string | undefined>();
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId);

  const handleSubmitSuccess = () => {
    onClose();
    onSuccess();
  };

  const { leaders, loading: leadersLoading } = useLeaders();

  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => ProjectService.getActiveProjects(),
    enabled: visible && recordType === 'worktime',
  });

  useEffect(() => {
    if (!visible || recordType !== 'worktime') return;
    worktimeForm.resetFields();
    setSelectedProjectId(projectId);
    setNewRecordWorkerId(undefined);
    worktimeForm.setFieldsValue({
      workDate: dayjs(),
      projectName,
      workUnits: 1,
    });
  }, [visible, recordType, projectId, projectName, worktimeForm]);

  const createWorktimeMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await fetch('/api/worker-module/history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || '新增失败');
      }
      return result;
    },
    onSuccess: () => {
      message.success('工时记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['worker-history'] });
      queryClient.invalidateQueries({ queryKey: ['worker-statistics'] });
      handleSubmitSuccess();
    },
    onError: (error: any) => {
      message.error(error?.message || '工时记录创建失败');
    },
  });

  const handleWorkerSelect = (worker: WorkerSearchResult) => {
    setNewRecordWorkerId(worker._id);
    worktimeForm.setFieldValue('unitPrice', worker.defaultPrice);
  };

  const handleProjectSelect = (_value: string, option: any) => {
    setSelectedProjectId(option.projectId || projectId);
  };

  const handleSubmitWorktime = async () => {
    const values = await worktimeForm.validateFields();
    const payload: any = {
      workDate: values.workDate.format('YYYY-MM-DD'),
      workerName: values.workerName,
      projectName: values.projectName,
      projectId: selectedProjectId || projectId,
      workContent: values.workContent,
      workUnits: values.workUnits,
      unitPrice: values.unitPrice,
      constructionType: values.constructionType,
      leaderId: values.leaderId,
    };
    if (newRecordWorkerId) {
      payload.workerId = newRecordWorkerId;
    }
    createWorktimeMutation.mutate(payload);
  };

  if (!recordType) return null;

  if (recordType === 'equipment') {
    return (
      <ConfigProvider theme={themeConfig}>
        <Modal
        title={`🔧 添加设备记录 - ${projectName}`}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={isMobile ? '100%' : 760}
        style={isMobile ? { top: 20, margin: '0 auto', maxWidth: '100%' } : {}}
        destroyOnClose
        styles={{
          header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
          body: { background: 'rgba(232, 237, 240, 0.94)' }
        }}
      >
        <EquipmentUsageForm
          initialValues={null}
          defaultProjectId={projectId}
          onSuccess={handleSubmitSuccess}
          onCancel={onClose}
        />
        </Modal>
      </ConfigProvider>
    );
  }

  if (recordType === 'material') {
    return (
      <ConfigProvider theme={themeConfig}>
        <Modal
        title={`📦 添加材料记录 - ${projectName}`}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={isMobile ? '100%' : 760}
        style={isMobile ? { top: 20, margin: '0 auto', maxWidth: '100%' } : {}}
        destroyOnClose
        styles={{
          header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
          body: { background: 'rgba(232, 237, 240, 0.94)' }
        }}
      >
        <MaterialUsageForm
          initialValues={null}
          defaultProjectId={projectId}
          onSuccess={handleSubmitSuccess}
          onCancel={onClose}
        />
        </Modal>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={themeConfig}>
      <Modal
      title={`👷 新增出工记录 - ${projectName}`}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmitWorktime}
      okText="保存"
      confirmLoading={createWorktimeMutation.isPending}
      width={isMobile ? '100%' : 600}
      style={isMobile ? { top: 20, margin: '0 auto', maxWidth: '100%' } : {}}
      destroyOnClose
      styles={{
        header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
        body: { background: 'rgba(232, 237, 240, 0.94)' },
        footer: { background: 'rgba(232, 237, 240, 0.98)', borderTop: '1px solid rgba(16, 27, 35, 0.08)' }
      }}
    >
      <Form form={worktimeForm} layout="vertical">
        <Form.Item
          label="工作日期"
          name="workDate"
          rules={[{ required: true, message: '请选择工作日期' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="工人名字"
          name="workerName"
          rules={[{ required: true, message: '请输入工人名字' }]}
        >
          <WorkerAutoComplete
            value={worktimeForm.getFieldValue('workerName') || ''}
            placeholder="请输入工人姓名"
            onChange={(name) => worktimeForm.setFieldValue('workerName', name)}
            onWorkerSelect={handleWorkerSelect}
            onClear={() => setNewRecordWorkerId(undefined)}
          />
        </Form.Item>

        <Form.Item
          label="项目名称"
          name="projectName"
          rules={[{ required: true, message: '请输入项目名称' }]}
        >
          <AutoComplete
            placeholder="请输入或选择项目名称"
            allowClear
            onSelect={handleProjectSelect}
            onChange={() => setSelectedProjectId(projectId)}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={(projectsData?.data as any[] || []).map((p: any) => ({
              value: p.name,
              label: p.name,
              projectId: p._id,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="工作内容"
          name="workContent"
          rules={[{ required: true, message: '请输入工作内容' }]}
        >
          <Input.TextArea rows={3} placeholder="请输入工作内容" />
        </Form.Item>

        <Form.Item
          label="工时（天）"
          name="workUnits"
          rules={[{ required: true, message: '请输入工时' }]}
        >
          <InputNumber min={0.1} max={2} step={0.1} style={{ width: '100%' }} placeholder="请输入工时" />
        </Form.Item>

        <Form.Item
          label="单价（元）"
          name="unitPrice"
          rules={[{ required: true, message: '请输入单价' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入单价" />
        </Form.Item>

        <Form.Item label="施工类型" name="constructionType">
          <Select
            allowClear
            placeholder="请选择施工类型"
            options={[
              { label: '园林', value: '园林' },
              { label: '土建', value: '土建' },
              { label: '外墙', value: '外墙' },
              { label: '其它', value: '其它' },
            ]}
          />
        </Form.Item>

        <Form.Item label="负责人" name="leaderId">
          <Select
            placeholder="请选择负责人"
            allowClear
            showSearch
            optionFilterProp="label"
            options={leaders.map((leader) => ({
              label: leader.name,
              value: leader._id,
            }))}
            loading={leadersLoading}
          />
        </Form.Item>
      </Form>
      </Modal>
    </ConfigProvider>
  );
};

export default AddRecordModal;
